import { c as _c } from "react/compiler-runtime";
import React from "react";
import { Tv } from "lucide-react";
import type { Labels } from "@/lib/labels/types";
import type { ResolvedListings } from "../utils/resolveMatchListings";
import { decideRowState } from "./renderState";
import { AllBroadcastersModalTrigger } from "./AllBroadcastersModal";
interface Props {
  resolved: ResolvedListings;
  labels: Labels;
  /** How many channel names to show inline before collapsing into "+N more". Default 4. */
  maxInline?: number;
}

/**
 * Full-width prominent banner showing broadcasters for the viewer's country:
 *
 *   ╔══════════════════════════════════════════════════════╗
 *   ║ 📺  Sky Sports · TNT Sports · DAZN · +2 more   [⌄] ║   (primary color bg)
 *   ╚══════════════════════════════════════════════════════╝
 *
 * - Primary-color background so it visually competes with the score header for attention.
 * - Country-filtered: only broadcasters for the viewer's locale-default country are inline.
 * - "+N more" / "See all" opens the modal with the full by-country list.
 * - Renders nothing when there are no listings at all for this match.
 */
export function MatchBroadcastersRow(t0) {
  const $ = _c(22);
  const {
    resolved,
    labels,
    maxInline: t1
  } = t0;
  const maxInline = t1 === undefined ? 4 : t1;
  let t2;
  if ($[0] !== resolved) {
    t2 = decideRowState(resolved);
    $[0] = resolved;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const state = t2;
  if (state === "hidden") {
    return null;
  }
  const defaultList = resolved.defaultCountryListings;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[2] !== defaultList || $[3] !== labels.tv.seeAllBroadcasters || $[4] !== labels.tv.watchOn || $[5] !== maxInline || $[6] !== state) {
    const inline = defaultList.slice(0, maxInline);
    const overflow = defaultList.length - inline.length;
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = ["match-broadcasters-row", "w-full flex items-center gap-3", "px-4 py-3 rounded-md", "[background-color:var(--color-primary)]", "[color:var(--color-primary-foreground,white)]", "shadow-sm"];
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    t3 = t7.join(" ");
    t4 = labels.tv.watchOn;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Tv className="w-5 h-5 shrink-0 opacity-90" aria-hidden="true" />;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t6 = state === "populated" ? <><span className="text-xs uppercase tracking-wide opacity-80 shrink-0 hidden sm:inline">{labels.tv.watchOn}</span><span className="flex-1 min-w-0 truncate text-sm font-medium">{inline.map(_temp)}{overflow > 0 && <span className="ml-2 opacity-80">+{overflow} more</span>}</span></> : <span className="flex-1 min-w-0 truncate text-sm italic opacity-90">{labels.tv.seeAllBroadcasters}</span>;
    $[2] = defaultList;
    $[3] = labels.tv.seeAllBroadcasters;
    $[4] = labels.tv.watchOn;
    $[5] = maxInline;
    $[6] = state;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[13] !== labels || $[14] !== resolved) {
    t7 = <AllBroadcastersModalTrigger resolved={resolved} labels={labels} onPrimary={true} />;
    $[13] = labels;
    $[14] = resolved;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = <section className={t3} aria-label={t4}>{t5}{t6}{t7}</section>;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}
function _temp(l, i) {
  return <React.Fragment key={l.id}>{i > 0 && <span className="mx-2 opacity-50">·</span>}<span>{l.channel.name}</span></React.Fragment>;
}